import { useEffect, useMemo, useRef, useState } from 'react';
import { Space, Spin, Alert, Button, Popconfirm, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { authClient } from '../../../../providers';
import { useCanEditCompany } from '../../api/editableCompaniesClient';
import {
  beginCompanySave,
  endCompanySave,
  useRunningCompanySave,
} from '../companySaveStore';
import { createSsoConfig, deleteSsoConfig, getCompanySsoConfig, patchSsoConfig } from './api';
import { SsoConfigForm, type SsoConfigFormState } from './SsoConfigForm';
import { SsoStatusBadge } from './SsoStatusBadge';
import { buildPatchPayload } from './buildPatchPayload';
import { validateSsoForm } from './validation';
import { formFromConfig } from './formFromConfig';
import { ssoStateOf, type SsoState } from './ssoState';
import type {
  SsoConfig,
  RoleToTypeRule,
  ExistingUserRolePolicy,
  ExistingUserLocationPolicy,
} from './types';

/**
 * SSO Configuration tab body (SF-3180, Tasks 1–4).
 *
 * Orchestrates:
 *  - Load the SSO config for the current company from sf-auth-service
 *    (API shape frozen in SF-3404).
 *  - Render the editable form + status badge.
 *  - Inline validation before save (mirrors backend validators — the
 *    backend stays authoritative; its error messages are also surfaced
 *    verbatim when a PATCH fails).
 *  - Secret-safe save: the masked "***" input is dropped on the wire by
 *    `buildPatchPayload`, so the stored secret is preserved when the
 *    operator edits other fields.
 *  - Delete with confirmation (AC7) — irreversible, audit-logged server-side.
 */
export interface SsoConfigTabProps {
  companyId: string;
  /**
   * SF-4656 — told the company's SSO state (none / off / live) after this
   * tab's own save or delete. The page uses it to keep the Feature Flags
   * SSO switch and this tab's visibility in step. Always passed the company
   * the save STARTED for: a delete that finishes after staff moved to another
   * company must not report about that other company.
   */
  onConfigChange?: (companyId: string, state: SsoState) => void;
  /** SF-4656 — named in messages that arrive after staff moved to another company. */
  companyName?: string;
  /**
   * SF-4656 — the page's own (newest) SSO state for this company. When it
   * disagrees with what this tab loaded, the setup changed elsewhere (another
   * admin created, deleted, or switched it), so the tab reloads to follow it.
   */
  pageSsoState?: SsoState;
}

const BUSY_TEXT = 'Another change for this company is still saving. Try again in a moment.';

export const SsoConfigTab: React.FC<SsoConfigTabProps> = ({
  companyId,
  onConfigChange,
  companyName,
  pageSsoState,
}) => {
  // SF-4656 — save and delete follow the company page's one-save-at-a-time
  // rule (companySaveStore), which outlives this tab: leave the company and
  // come back while a save runs, and the tab stays locked, then reloads the
  // setup once that save is done (so a second save can't send the old,
  // pre-save role mappings and policies on top of it).
  const runningSave = useRunningCompanySave(companyId);
  const companyBusy = runningSave !== null;
  const [reloadKey, setReloadKey] = useState(0);
  // An SSO save already running when the tab mounts was not started by it.
  // Only SSO saves trigger the reload: a company-field save (Account Type,
  // a feature switch) must not throw away unsaved edits in this form (review).
  const sawOtherSave = useRef(runningSave?.kind === 'sso');
  const ownSave = useRef(false);
  useEffect(() => {
    if (runningSave?.kind === 'sso' && !ownSave.current) sawOtherSave.current = true;
    if (!runningSave && sawOtherSave.current) {
      sawOtherSave.current = false;
      setReloadKey((k) => k + 1);
    }
  }, [runningSave]);
  const configRef = useRef<SsoConfig | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const say = (text: string) =>
    mounted.current ? text : `${companyName ?? 'Another company'}: ${text}`;
  // Latest callback without making the load effect re-run on every render.
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;
  // SF-3404 blocker fix: gate write/delete UI by permission. The tab
  // itself is hidden when the user lacks `admin:sso:read` (see AC8 in
  // companies/show.tsx), but a user with read-only access could still
  // be navigated here directly — we must not give them a Save or Delete
  // button. The server enforces these permissions too; this is
  // belt-and-braces UX so read-only operators see the right affordances.
  const { data: canWriteData, isLoading: canWriteLoading } = useCan({
    resource: 'sso',
    action: 'edit',
  });
  const { data: canDeleteData, isLoading: canDeleteLoading } = useCan({
    resource: 'sso',
    action: 'delete',
  });
  // SF-4668: staff with a company list may only change the companies on it.
  const { canEditCompany, isLoading: scopeLoading } = useCanEditCompany(companyId);
  const canWrite = canWriteData?.can === true && canEditCompany;
  const canDelete = canDeleteData?.can === true && canEditCompany;
  const permissionsLoading = canWriteLoading || canDeleteLoading || scopeLoading;

  const [loading, setLoading] = useState(true);
  // Follow the page's newest SSO state when it disagrees with what this tab
  // shows (another admin created, deleted or switched the setup). Never while
  // the tab is loading or any save runs: it re-checks once those finish, so
  // an answer that arrived meanwhile is not lost (Bugbot). Each page answer is
  // followed at most once, so a disagreement a reload can't fix can't loop.
  const followedAnswer = useRef<SsoState | undefined>(undefined);
  useEffect(() => {
    if (pageSsoState === undefined || loading || companyBusy) return;
    if (followedAnswer.current === pageSsoState) return;
    followedAnswer.current = pageSsoState;
    if (pageSsoState !== ssoStateOf(configRef.current)) setReloadKey((k) => k + 1);
  }, [pageSsoState, loading, companyBusy]);
  const [config, setConfig] = useState<SsoConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<SsoConfigFormState>(() => formFromConfig(null));
  // Role-mapping rules (Task 2).
  const [roleRules, setRoleRules] = useState<RoleToTypeRule[]>([]);
  // Policy + default-type (Task 3). Defaults: 'preserve' + 'employee'
  // (safest non-destructive + narrowest permission).
  const [policy, setPolicy] = useState<ExistingUserRolePolicy>('preserve');
  // SF-3593 — existingUserLocationPolicy. Default 'preserve' (non-destructive
  // — locations are typically managed in ServeFirst, not the IdP).
  const [locationPolicy, setLocationPolicy] = useState<ExistingUserLocationPolicy>('preserve');
  const [defaultType, setDefaultType] = useState<string>('employee');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // `submitted` flips on first save attempt so we don't yell at the
  // operator about empty fields before they've even started typing.
  const [submitted, setSubmitted] = useState(false);
  configRef.current = config;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCompanySsoConfig(authClient, companyId)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        // Not reported to the page: the page has its own, newer check, and an
        // older load answer must not overwrite it (Bugbot). Only this tab's
        // own create/delete are reported.
        setForm(formFromConfig(c));
        setRoleRules(c?.roleToType ?? []);
        setPolicy(c?.existingUserRolePolicy ?? 'preserve');
        setLocationPolicy(c?.existingUserLocationPolicy ?? 'preserve');
        setDefaultType(c?.defaultType ?? 'employee');
        setSubmitted(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          setError('You do not have permission to view SSO configuration.');
        } else {
          setError('Failed to load SSO configuration. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, reloadKey]);

  const validation = useMemo(
    () =>
      validateSsoForm({
        name: form.name,
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        hasExistingSecret: !!config,
        discoveryUrl: form.discoveryUrl,
        domainsRaw: form.domainsRaw,
        scopesRaw: form.scopesRaw,
        provider: form.provider,
        pingOneRegion: form.pingOneRegion,
        pingOneEnvironmentId: form.pingOneEnvironmentId,
        defaultType,
      }),
    [form, defaultType, config],
  );

  // Until the operator attempts a save, suppress "required" noise — show
  // nothing. After the first attempt, surface errors so they see why the
  // save was blocked.
  const shownErrors = submitted ? validation.errors : {};

  const handleFormChange = (patch: Partial<SsoConfigFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!validation.isValid) {
      message.error('Fix the highlighted fields before saving.');
      return;
    }
    const forCompany = companyId;
    const token = beginCompanySave(forCompany, { kind: 'sso' });
    if (token === null) {
      message.warning(BUSY_TEXT);
      return;
    }
    ownSave.current = true;
    setSaving(true);
    try {
      const payload = buildPatchPayload(
        {
          roleToType: roleRules,
          existingUserRolePolicy: policy,
          existingUserLocationPolicy: locationPolicy,
          defaultType,
          ...form,
        },
        config,
      );
      let updated: SsoConfig;
      if (!config) {
        // AC2 — create flow: companyId is server-authoritative and not
        // diff-emitted by buildPatchPayload, so we inject it here.
        updated = await createSsoConfig(authClient, {
          ...payload,
          companyId,
        } as Partial<SsoConfig>);
      } else {
        updated = await patchSsoConfig(authClient, config.id, payload);
      }
      setConfig(updated);
      onConfigChangeRef.current?.(forCompany, ssoStateOf(updated));
      setForm(formFromConfig(updated));
      setRoleRules(updated.roleToType ?? []);
      setPolicy(updated.existingUserRolePolicy ?? 'preserve');
      setLocationPolicy(updated.existingUserLocationPolicy ?? 'preserve');
      setDefaultType(updated.defaultType ?? 'employee');
      setSubmitted(false);
      message.success(say(config ? 'SSO configuration saved.' : 'SSO configuration created.'));
    } catch (err: unknown) {
      // sf-auth-service returns { error: { code, message } } on 4xx.
      // Surface the human-readable message when present — it carries the
      // backend's authoritative reason (e.g. SSO_DOMAIN_CONFLICT, regex
      // ReDoS rejection, unknown user type).
      const serverMsg = (
        err as {
          response?: { data?: { error?: { message?: string } } };
        }
      )?.response?.data?.error?.message;
      message.error(
        say(
          serverMsg
            ? `Failed to save SSO configuration: ${serverMsg}`
            : config
              ? 'Failed to save SSO configuration. Please try again.'
              : 'Failed to create SSO configuration. Please try again.',
        ),
      );
    } finally {
      ownSave.current = false;
      endCompanySave(forCompany, token);
      if (mounted.current) setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!config) return;
    const forCompany = companyId;
    const token = beginCompanySave(forCompany, { kind: 'sso' });
    if (token === null) {
      message.warning(BUSY_TEXT);
      return;
    }
    ownSave.current = true;
    setDeleting(true);
    try {
      await deleteSsoConfig(authClient, config.id);
      setConfig(null);
      onConfigChangeRef.current?.(forCompany, 'none');
      setForm(formFromConfig(null));
      setRoleRules([]);
      setPolicy('preserve');
      setLocationPolicy('preserve');
      setDefaultType('employee');
      setSubmitted(false);
      message.success(say('SSO configuration deleted.'));
    } catch (err: unknown) {
      const serverMsg = (
        err as {
          response?: { data?: { error?: { message?: string } } };
        }
      )?.response?.data?.error?.message;
      message.error(
        say(
          serverMsg
            ? `Failed to delete SSO configuration: ${serverMsg}`
            : 'Failed to delete SSO configuration. Please try again.',
        ),
      );
    } finally {
      ownSave.current = false;
      endCompanySave(forCompany, token);
      if (mounted.current) setDeleting(false);
    }
  };

  if (loading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', margin: 40 }} />;
  }

  if (error) {
    return <Alert type="error" showIcon message={error} />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <SsoStatusBadge configured={!!config} enabled={config?.isEnabled} />
      <SsoConfigForm
        config={config}
        form={form}
        onFormChange={handleFormChange}
        roleRules={roleRules}
        onRoleRulesChange={setRoleRules}
        policy={policy}
        onPolicyChange={setPolicy}
        locationPolicy={locationPolicy}
        onLocationPolicyChange={setLocationPolicy}
        defaultType={defaultType}
        onDefaultTypeChange={setDefaultType}
        errors={shownErrors}
        disabled={saving || deleting || permissionsLoading || !canWrite || companyBusy}
      />
      {!permissionsLoading && !canWrite && (
        <Alert
          type="info"
          showIcon
          message="Read-only access"
          description="You have permission to view this SSO configuration but not to modify it. Contact an administrator if you need to make changes."
          style={{ marginTop: 8 }}
        />
      )}
      <Space>
        {canWrite && (
          <Button
            type="primary"
            loading={saving}
            disabled={saving || deleting || permissionsLoading || companyBusy}
            onClick={handleSave}
            aria-label={config ? 'Save SSO configuration' : 'Create SSO configuration'}
          >
            {config ? 'Save' : 'Create SSO configuration'}
          </Button>
        )}
        {config && canDelete && (
          <Popconfirm
            title="Delete SSO configuration?"
            description="This permanently removes the SSO config for this company. Existing SSO users will no longer be able to sign in via this provider. This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleting }}
            cancelText="Cancel"
            onConfirm={handleDelete}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={saving || deleting || permissionsLoading || companyBusy}
              aria-label="Delete SSO configuration"
            >
              Delete
            </Button>
          </Popconfirm>
        )}
      </Space>
    </Space>
  );
};
